<template>
	<div class="cabinet-login-view">
		<LoginHeader />
		<div class="body margin-general">
			<instructions
				:hideIntructionsMobile="hideIntructionsMobile"
				type="login"
			/>
			<LoginBox
				ref="loginBox"
				:auto="true"
				:isShowAlert="isAlertShown"
				@submit="doSignIn"
				@forgot="forgot"
			/>
		</div>

		<div class="footer margin-general">
			<FooterDefault />
		</div>

		<!-- Alert -->
		<TVAlert v-if="isShowAlert" :detail="dialogMessageUser" severity="error" />

		<div class="modal-general withdraw" v-if="showTOTPDialog">
			<div class="mask"></div>
			<div class="modal">
				<div class="modal-container informative-modal" style="gap: 2rem">
					<div class="modal-header" style="gap: 1rem">
						<div class="icon info" style="color: #344555">
							<span
								class="material-symbols-outlined"
								translate="no"
								style="font-size: 2rem"
							>
								password
							</span>
						</div>
						<div class="align">
							<p class="subtitle-3">{{ $t('loginPage.enterYourCode') }}</p>
						</div>
						<div class="modal-content">
							<p class="body-4">
								{{ $t('loginPage.enterCodeBelow') }}
							</p>
							<div
								style="
									text-align: left;
									display: flex;
									flex-direction: column;
									gap: 0.5rem;
								"
							>
								<span class="body-4"> {{ $t('loginPage.authCode') }} </span>
								<OtpInput :length="6" @otp-entered="handleOtpEntered" />

								<p v-if="dialogErrorMessage" style="color: red">
									{{ dialogErrorMessage }}
								</p>
							</div>
						</div>
					</div>

					<div class="modal-buttons" @click="totpLogin">
						<div class="btn-primary-small">
							<p class="body-4-bold">{{ $t('loginBox.submit') }}</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { Auth } from 'aws-amplify'
import LoginHeader from '@/components/login/LoginHeader'
import LoginBox from '@/components/login/LoginBox'
import Modal from '@/components/Modal'
import {
	getCurrentInstance,
	onMounted,
	onUnmounted,
	ref,
	computed,
} from '@vue/runtime-core'
import store from '@/store'
import { useRoute, useRouter } from 'vue-router'
import FooterDefault from '../../components/cabinet/default/FooterDefault.vue'
import TVAlert from '../../../modules/global/TVAlert.vue'
import OtpInput from '@/components/settings_new/atoms/OTPInput.vue'
import Instructions from '@/components/register/Instructions.vue'

export default {
	mounted() {
		const pageTitle = 'Tradeview Markets Login - Access Your Trading Account'
		const metaContent =
			'Log in to your Tradeview Markets account to access trading tools, analytics, and resources. Stay connected to the markets and manage your portfolio with ease.'

		document.title = pageTitle

		// Meta tags array
		const metaTags = [
			{ name: 'title', content: pageTitle },
			{ name: 'description', content: metaContent },
			{ name: 'twitter:title', content: pageTitle },
			{ name: 'twitter:description', content: metaContent },
			{ property: 'og:title', content: pageTitle },
			{ property: 'og:description', content: metaContent },
		]

		// Set or create meta tags
		metaTags.forEach(({ name, property, content }) => {
			let metaTag = document.querySelector(
				`meta[name="${name}"], meta[property="${property}"]`,
			)
			if (metaTag) {
				metaTag.content = content
			} else {
				metaTag = document.createElement('meta')
				if (name) {
					metaTag.name = name
				}
				if (property) {
					metaTag.property = property
				}
				metaTag.content = content
				document.head.appendChild(metaTag)
			}
		})
	},

	name: 'Login',
	emits: ['onError'],

	components: {
		LoginHeader,
		LoginBox,
		Modal,
		FooterDefault,
		OtpInput,
		TVAlert,
		Instructions,
	},
	setup(props) {
		let alertIsShown = ref(null)
		let dialogMessage = ref(null)
		let dialogMessageUser = ref(null)
		let dialogErrorMessage = ref(null)
		let showDialog = ref(false)
		let showDialogUser = ref(false)
		let showTOTPDialog = ref(false)
		let totpCode = ref(null)
		let newCode = ref(null)
		let forgotEmail = ref(null)
		let attemptedEmail = ref(null)
		let attemptedPassword = ref(null)
		let dialogErrorMesssage = ref(null)
		const isShowAlert = ref(false)
		const loginBox = ref(null)

		const internalInstance = getCurrentInstance()
		const emitter = internalInstance.appContext.config.globalProperties.emitter

		const handleOtpEntered = (code) => {
			newCode.value = code.trim()
			totpCode.value = code
		}

		onMounted(() => {
			store.commit('setLoginFlow', 'LOGIN')

			emitter.on('showDialogUser', (message) => {
				if (
					message.includes('Cannot read properties of') ||
					message.includes('WEBPACK')
				) {
					dialogMessageUser.value = ''
					isShowAlert.value = false
				} else {
					dialogMessageUser.value = message
					alertIsShown.value = true
					isShowAlert.value = true
				}
			})

			emitter.on('showTOTPDialog', (message) => {
				dialogMessage.value = message
				dialogErrorMessage.value = null
				showTOTPDialog.value = true
			})
			emitter.on('showDialog', (message) => {
				dialogMessage.value = message
				showDialog.value = true
			})

			emitter.on('showCodeDialogAtLogin', (message) => {
				dialogMessage.value = message
				dialogErrorMessage.value = null
			})

			emitter.on('showCodeDialogErrorMessageAtLogin', (message) => {
				dialogErrorMessage.value = message
			})

			emitter.on('closeAllRegisterDialogs', () => {
				showDialog.value = false
			})

			const htmlLang = document.querySelector('html').getAttribute('lang')
			if (htmlLang === 'ar') {
				document.documentElement.setAttribute('dir', 'rtl')
			} else {
				document.documentElement.setAttribute('dir', 'ltr')
			}
		})

		onUnmounted(() => {
			emitter.off('showDialog')
			emitter.off('showDialogUser')
			emitter.off('showTOTPDialog')
			emitter.off('showCodeDialogAtLogin')
			emitter.off('closeAllRegisterDialogs')
		})

		const forgot = (email) => {
			emitter.emit('requestForgotPassword', {
				email: email,
			})

			forgotEmail.value = email
			loginBox.value.release()
		}

		const totpLogin = async () => {
			showTOTPDialog.value = false
			loginBox.value.lock()
			emitter.emit('requestTOTPLogin', {
				totpCode: totpCode,
			})
		}

		const doSignIn = (email, password) => {
			showDialog.value = false
			attemptedEmail.value = email
			attemptedPassword.value = password
			isShowAlert.value = false

			if (
				email != null &&
				password != null &&
				email.length > 0 &&
				password.length > 0
			) {
				emitter.emit('requestSignIn', {
					email: email,
					password: password,
				})
			} else {
				isShowAlert.value = true
				loginBox.value.release()
			}
		}

		const isAlertShown = computed(() => alertIsShown.value)
		return {
			loginBox,
			forgot,
			totpLogin,
			doSignIn,
			dialogMessage,
			dialogMessageUser,
			dialogErrorMessage,
			showDialog,
			showDialogUser,
			showTOTPDialog,
			totpCode,
			newCode,
			forgotEmail,
			attemptedEmail,
			dialogErrorMesssage,
			isShowAlert,
			handleOtpEntered,
			alertIsShown,
			isAlertShown,
		}
	},
}
</script>
